import { CalendarIcon } from '@heroicons/react/24/outline'
import { Button, Card, Chip, Input, Select, SelectItem } from '@heroui/react'
import posthog from 'posthog-js'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSnapshot } from 'valtio'
import { logError } from '@/components/Error/Error'
import type { Item } from '../../../../types'
import { Flag } from '../../../../types'
import { RichTextEditor } from '../../components/settings/RichTextEditor'
import { api } from '../../lib/api'
import { state } from '../../lib/state'

// Interface for license check response
interface LicenseCheckResponse {
  canAdd: boolean
  currentCount: number
  maxItems: number
}

const TitleInput: React.FC<React.ComponentProps<typeof Input>> = props => {
  return (
    <Input
      {...props}
      id="title"
      classNames={{
        ...props.classNames,
        input: `${props.classNames?.input || ''} caret-[var(--ds-text)] outline-none`
      }}
    />
  )
}

const ReleaseDateInput: React.FC<
  React.ComponentProps<typeof Input>
> = props => {
  return (
    <div className="relative">
      <Input
        {...props}
        id="release-input"
        classNames={{
          ...props.classNames,
          input: `${props.classNames?.input || ''} caret-[var(--ds-text)] outline-none`
        }}
      />
      <CalendarIcon
        className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--ds-text-subtle)] pointer-events-none"
        aria-hidden="true"
      />
    </div>
  )
}

// Helper to parse ISO date as local date
function parseLocalDate(isoDateStr: string) {
  const [year, month, day] = isoDateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const AdminNewItemPage = () => {
  const { radarConfig, currentRadarId } = useSnapshot(state)
  const navigate = useNavigate()
  const [newItem, setNewItem] = useState<{
    title: string
    quadrant: string
    ring: string
    release: string
    flag: Flag | ''
    body: string
    tags: string[]
  }>({
    title: '',
    quadrant: radarConfig?.quadrants[0]?.id || '',
    ring: radarConfig?.rings[0]?.id || '',
    release: new Date().toISOString().split('T')[0],
    flag: Flag.New,
    body: '',
    tags: []
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [licenseInfo, setLicenseInfo] = useState<LicenseCheckResponse | null>(
    null
  )

  const onClose = () => navigate('/admin/items')

  // Check license status on component mount
  useEffect(() => {
    const checkLicense = async () => {
      try {
        const licenseCheck = await api.checkLicenseLimit(
          currentRadarId || undefined
        )
        setLicenseInfo(licenseCheck)

        // Check if licenseCheck exists and has canAdd property before accessing it
        if (licenseCheck && !licenseCheck.canAdd) {
          setError(
            `You've reached the limit of ${licenseCheck.maxItems} radar items in the unlicensed version. Please upgrade to add more items.`
          )
        }
      } catch (err) {
        if (posthog)
          posthog.capture('error', {
            message: 'Failed to check license',
            error: err
          })
        logError('NewItemPage', 'Failed to check license:', err)
      }
    }

    if (currentRadarId) {
      checkLicense()
    }
  }, [currentRadarId])

  useEffect(() => {
    // Add the CSS for dropdowns to the document head
    const styleElement = document.createElement('style')
    styleElement.textContent = `
      .dropdown-content {
        --dropdown-bg: #ffffff;
        --dropdown-text: #172B4D;
        --dropdown-border: #DFE1E6;
        --dropdown-hover: #F4F5F7;
        --dropdown-selected: #EBECF0;
        --dropdown-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }
    
      @media (prefers-color-scheme: dark) {
        .dropdown-content {
          --dropdown-bg: #282E33;
          --dropdown-text: #FFFFFF;
          --dropdown-border: #505050;
          --dropdown-hover: #3E474F;
          --dropdown-selected: #353D45;
          --dropdown-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
        }
      }
    
      .dropdown-content {
        background-color: var(--dropdown-bg) !important;
        color: var(--dropdown-text) !important;
        border: 1px solid var(--dropdown-border) !important;
        box-shadow: var(--dropdown-shadow) !important;
      }
    
      .dropdown-item {
        background-color: var(--dropdown-bg) !important;
        color: var(--dropdown-text) !important;
        transition: background-color 0.2s ease;
      }
    
      .dropdown-item:hover {
        background-color: var(--dropdown-hover) !important;
      }
    
      .dropdown-item[data-selected=true] {
        background-color: var(--dropdown-selected) !important;
      }
    `
    document.head.appendChild(styleElement)

    // Cleanup function to remove the style element when component unmounts
    return () => {
      document.head.removeChild(styleElement)
    }
  }, []) // Empty dependency array means this runs once on mount

  // Helper function to calculate position for a new item
  const calculatePosition = (
    quadrant: string,
    ring: string
  ): [number, number] => {
    if (!radarConfig) return [0, 0]

    const quadrantConfig = radarConfig.quadrants.find(q => q.id === quadrant)
    const ringConfig = radarConfig.rings.find(r => r.id === ring)
    if (!quadrantConfig || !ringConfig) return [0, 0]

    // Get the ring's radius (as a percentage of the chart)
    const ringRadius = ringConfig.radius || 0.5
    const prevRingRadius =
      radarConfig.rings[radarConfig.rings.indexOf(ringConfig) - 1]?.radius || 0

    // Calculate a random position within the ring's sector
    const minRadius = prevRingRadius
    const maxRadius = ringRadius
    const radius = minRadius + (maxRadius - minRadius) * 0.5 // Place it in the middle of the ring

    // Calculate angle based on quadrant position (in radians)
    const baseAngle = (quadrantConfig.position - 1) * 90 // 0, 90, 180, or 270
    const randomAngle = baseAngle + 45 // Place it in the middle of the quadrant

    // Convert polar coordinates to cartesian
    const angleInRadians = (randomAngle * Math.PI) / 180
    const x = Math.round(400 + radius * 400 * Math.cos(angleInRadians))
    const y = Math.round(400 + radius * 400 * Math.sin(angleInRadians))

    return [x, y]
  }

  const handleCreateItem = async () => {
    // Validate required fields before proceeding
    if (!newItem.title.trim()) {
      setError('Please enter a title for the new item.')
      return
    }
    if (!newItem.quadrant || !newItem.ring) {
      setError('Please select a quadrant and ring for the new item.')
      return
    }
    try {
      // Check if we can add more items - ensure licenseInfo exists before accessing properties
      if (licenseInfo && !licenseInfo.canAdd) {
        setError(
          `You've reached the limit of ${licenseInfo.maxItems} radar items in the unlicensed version. Please upgrade to add more items.`
        )
        return
      }
      setIsSubmitting(true)
      const position = calculatePosition(newItem.quadrant, newItem.ring)

      const item: Item = {
        id: crypto.randomUUID(),
        quadrant: newItem.quadrant,
        tags: newItem.tags,
        releases: [
          {
            id: crypto.randomUUID(),
            title: newItem.title,
            body: newItem.body,
            releaseDate: newItem.release,
            ring: newItem.ring,
            flag: newItem.flag || Flag.New,
            position
          }
        ]
      }

      // First get the current data
      const currentData = await api.getRadarData(currentRadarId || undefined)

      // Check again if adding this item would exceed the limit - ensure licenseInfo exists
      if (
        licenseInfo &&
        currentData.items.length >= (licenseInfo.maxItems || 10) &&
        !licenseInfo.canAdd
      ) {
        setError(
          `You've reached the limit of ${licenseInfo?.maxItems || 10} radar items in the unlicensed version. Please upgrade to add more items.`
        )
        setIsSubmitting(false)
        return
      }

      if (!currentRadarId) {
        console.warn(
          '[NewItemPage] currentRadarId is undefined! Aborting item creation.'
        )
        setError('Cannot create item: No radar selected.')
        setIsSubmitting(false)
        return
      }
      const plainItems = [
        ...currentData.items,
        { ...item, archived: false }
      ].map(i => ({ ...i }))
      await api.setRadarData({ items: plainItems }, currentRadarId)
      state.radarItems.splice(0, state.radarItems.length, ...plainItems)
      onClose()
    } catch (err) {
      logError('NewItemPage', 'Error creating item:', err)
      setError(err instanceof Error ? err.message : 'Failed to create item')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Add a function to handle tag removal
  const handleRemoveTag = (tagToRemove: string) => {
    setNewItem(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  // Add a function to handle tag input
  const handleTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const input = e.target as HTMLInputElement
      const value = input.value.trim().toUpperCase()
      if (value && !newItem.tags.some(tag => tag === value)) {
        setNewItem(prev => ({
          ...prev,
          tags: [...prev.tags, value]
        }))
        input.value = ''
      }
    } else if (
      e.key === 'Backspace' &&
      (e.target as HTMLInputElement).value === ''
    ) {
      // If backspace is pressed and the input is empty, remove the last tag
      e.preventDefault()
      if (newItem.tags.length > 0) {
        setNewItem(prev => ({
          ...prev,
          tags: prev.tags.slice(0, -1)
        }))
      }
    }
  }

  const handleTagBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim().toUpperCase()
    if (value && !newItem.tags.some(tag => tag === value)) {
      setNewItem(prev => ({
        ...prev,
        tags: [...prev.tags, value]
      }))
      e.target.value = ''
    }
  }

  if (!radarConfig) {
    return (
      <Card className="border border-[var(--ds-border)] bg-[var(--ds-surface)] rounded-[3px] shadow-none p-6 my-6">
        <div className="flex items-center justify-center">
          <div className="text-[var(--ds-text-subtle)]">Loading...</div>
        </div>
      </Card>
    )
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-item-page">
      <Card
        className="border border-[var(--ds-border)] bg-[var(--ds-surface)] rounded-[3px] shadow-none p-6 my-6"
        data-testid="new-item-form-card"
        aria-labelledby="new-item-title"
      >
        <div className="flex justify-between items-center mb-6">
          <h2
            className="text-xl font-semibold text-[var(--ds-text)]"
            id="new-item-title"
          >
            Add New Tech Radar Item
          </h2>
        </div>

        <fieldset className="space-y-6" aria-label="Item details form">
          <div>
            <label
              htmlFor="title"
              className="text-[var(--ds-text)] text-sm"
              id="title-label"
            >
              Title <span className="text-red-500">*</span>
            </label>
            <TitleInput
              value={newItem.title}
              placeholder="Enter title..."
              required
              onChange={e =>
                setNewItem(prev => ({
                  ...prev,
                  title: e.target.value
                }))
              }
              classNames={{
                label: 'text-[var(--ds-text)] text-sm mb-1.5',
                input:
                  'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none',
                inputWrapper:
                  'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] data-[focused=true]:border-[var(--ds-border-focused)]'
              }}
              data-testid="title-input"
              aria-labelledby="title-label"
              aria-required="true"
            />
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div>
              <label
                htmlFor="release-input"
                className="text-[var(--ds-text)] text-sm"
                id="release-date-label"
              >
                Release Date
              </label>
              <ReleaseDateInput
                type="date"
                value={newItem.release}
                onChange={e => {
                  const date = parseLocalDate(e.target.value)
                  // Format as YYYY-MM-DD
                  const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                  setNewItem(prev => ({
                    ...prev,
                    release: formattedDate
                  }))
                }}
                classNames={{
                  input:
                    'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none',
                  inputWrapper:
                    'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] data-[focused=true]:border-[var(--ds-border-focused)]'
                }}
                data-testid="release-date-input"
                aria-labelledby="release-date-label"
                aria-required="true"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="quadrant-select"
                className="text-[var(--ds-text)] text-sm"
                id="quadrant-label"
              >
                Quadrant
              </label>
              <Select
                aria-label="Quadrant selection"
                selectedKeys={newItem.quadrant ? [newItem.quadrant] : []}
                selectionMode="single"
                onSelectionChange={keys => {
                  const selected = Array.from(keys)[0]?.toString()
                  if (selected)
                    setNewItem(prev => ({
                      ...prev,
                      quadrant: selected
                    }))
                }}
                variant="flat"
                classNames={{
                  base: 'bg-transparent rounded-sm',
                  trigger: `h-10 min-h-0 px-2 py-0.5 text-black dark:text-white bg-transparent border border-[var(--ds-border)] hover:border-blue-500 hover:!bg-transparent data-[hover=true]:!bg-transparent items-center transition-none w-full justify-between rounded-[3px] text-[var(--ds-text)] focus:border-[var(--ds-border-focused)] cursor-pointer`,
                  value: 'truncate',
                  listbox: 'bg-[var(--ds-surface)] shadow-lg',
                  popoverContent: 'bg-[var(--ds-surface)]'
                }}
                style={{ color: 'var(--ds-text)', borderWidth: '1px' }}
                renderValue={selected => {
                  const key = selected?.[0]
                  const option = radarConfig.quadrants.find(q => q.id === key)
                  return (
                    <span style={{ color: 'var(--ds-text)' }}>
                      {option?.title ||
                        radarConfig.quadrants.find(
                          q => q.id === newItem.quadrant
                        )?.title ||
                        ''}
                    </span>
                  )
                }}
                placeholder={
                  radarConfig.quadrants.find(q => q.id === newItem.quadrant)
                    ?.title || 'Select Quadrant'
                }
                data-testid="quadrant-dropdown"
                fullWidth={true}
                radius="sm"
                endContent={(() => {
                  const option = radarConfig.quadrants.find(
                    q => q.id === newItem.quadrant
                  )
                  return option ? (
                    <span
                      className="w-1 h-4 rounded-xs ml-2"
                      style={{
                        backgroundColor: option.color,
                        display: 'inline-block'
                      }}
                      aria-hidden="true"
                    />
                  ) : null
                })()}
              >
                {radarConfig.quadrants.map(quadrant => (
                  <SelectItem
                    key={quadrant.id}
                    className="dropdown-item"
                    classNames={{ selectedIcon: 'flex items-center' }}
                    data-testid={`quadrant-option-${quadrant.id}`}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-1 h-4 rounded-xs mr-2"
                        style={{ backgroundColor: quadrant.color }}
                        aria-hidden="true"
                      />
                      {quadrant.title}
                    </div>
                  </SelectItem>
                ))}
              </Select>
            </div>

            <div>
              <label
                htmlFor="ring-select"
                className="text-[var(--ds-text)] text-sm"
                id="ring-label"
              >
                Ring
              </label>
              <Select
                aria-label="Ring selection"
                selectedKeys={newItem.ring ? [newItem.ring] : []}
                selectionMode="single"
                onSelectionChange={keys => {
                  const selected = Array.from(keys)[0]?.toString()
                  if (selected)
                    setNewItem(prev => ({
                      ...prev,
                      ring: selected
                    }))
                }}
                variant="flat"
                classNames={{
                  base: 'bg-transparent rounded-sm',
                  trigger: `h-10 min-h-0 px-2 py-0.5 text-black dark:text-white bg-transparent border border-[var(--ds-border)] hover:border-blue-500 hover:!bg-transparent data-[hover=true]:!bg-transparent items-center transition-none w-full justify-between rounded-[3px] text-[var(--ds-text)] focus:border-[var(--ds-border-focused)] cursor-pointer`,
                  value: 'truncate',
                  listbox:
                    'bg-[var(--ds-surface)] shadow-lg',
                  popoverContent: 'bg-[var(--ds-surface)]'
                }}
                style={{ color: 'var(--ds-text)', borderWidth: '1px' }}
                renderValue={selected => {
                  const key = selected?.[0]
                  const option = radarConfig.rings.find(r => r.id === key)
                  return (
                    <span style={{ color: 'var(--ds-text)' }}>
                      {option?.title ||
                        radarConfig.rings.find(r => r.id === newItem.ring)
                          ?.title ||
                        ''}
                    </span>
                  )
                }}
                placeholder={
                  radarConfig.rings.find(r => r.id === newItem.ring)?.title ||
                  'Select Ring'
                }
                data-testid="ring-dropdown"
                fullWidth={true}
                radius="sm"
                endContent={(() => {
                  const option = radarConfig.rings.find(
                    r => r.id === newItem.ring
                  )
                  return option ? (
                    <span
                      className="w-1 h-4 rounded-xs ml-2"
                      style={{
                        backgroundColor: option.color,
                        display: 'inline-block'
                      }}
                      aria-hidden="true"
                    />
                  ) : null
                })()}
              >
                {radarConfig.rings.map(ring => (
                  <SelectItem
                    key={ring.id}
                    className="dropdown-item"
                    classNames={{ selectedIcon: 'flex items-center' }}
                    data-testid={`ring-option-${ring.id}`}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-1 h-4 rounded-xs mr-2"
                        style={{ backgroundColor: ring.color }}
                        aria-hidden="true"
                      />
                      {ring.title}
                    </div>
                  </SelectItem>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <label
              htmlFor="status-select"
              className="text-[var(--ds-text)] text-sm"
              id="status-label"
            >
              Status
            </label>
            <Select
              aria-label="Status selection"
              selectedKeys={newItem.flag ? [newItem.flag] : []}
              selectionMode="single"
              onSelectionChange={keys => {
                const key = Array.from(keys)[0]?.toString()
                if (key)
                  setNewItem(prev => ({
                    ...prev,
                    flag: key as Flag
                  }))
              }}
              variant="flat"
              classNames={{
                base: 'bg-transparent rounded-sm',
                trigger: `h-10 min-h-0 px-2 py-0.5 text-black dark:text-white bg-transparent border border-[var(--ds-border)] hover:border-blue-500 hover:!bg-transparent data-[hover=true]:!bg-transparent items-center transition-none w-full justify-between rounded-[3px] text-[var(--ds-text)] focus:border-[var(--ds-border-focused)] cursor-pointer`,
                value: 'truncate',
                listbox:
                  'bg-[var(--ds-surface)] shadow-lg',
                popoverContent: 'bg-[var(--ds-surface)]'
              }}
              style={{ color: 'var(--ds-text)', borderWidth: '1px' }}
              renderValue={selected => {
                const key = selected?.[0]
                const flag =
                  radarConfig.flags[key as keyof typeof radarConfig.flags]
                return (
                  <span style={{ color: 'var(--ds-text)' }}>
                    {flag?.title ||
                      radarConfig.flags[
                        newItem.flag as keyof typeof radarConfig.flags
                      ]?.title ||
                      ''}
                  </span>
                )
              }}
              placeholder={
                radarConfig.flags[
                  newItem.flag as keyof typeof radarConfig.flags
                ]?.title || 'Select Status'
              }
              data-testid="status-dropdown"
              fullWidth={true}
              radius="sm"
              endContent={(() => {
                const flag =
                  radarConfig.flags[
                    newItem.flag as keyof typeof radarConfig.flags
                  ]
                return flag ? (
                  <span
                    className="w-1 h-4 rounded-xs ml-2"
                    style={{
                      backgroundColor: flag.color,
                      display: 'inline-block'
                    }}
                    aria-hidden="true"
                  />
                ) : null
              })()}
            >
              {Object.entries(radarConfig.flags).map(([key, flag]) => (
                <SelectItem
                  key={key}
                  className="dropdown-item"
                  classNames={{ selectedIcon: 'flex items-center' }}
                  data-testid={`status-option-${key}`}
                >
                  <div className="flex items-center">
                    <div
                      className="w-1 h-4 rounded-xs mr-2"
                      style={{ backgroundColor: flag.color }}
                      aria-hidden="true"
                    />
                    <span className="capitalize">{flag.title}</span>
                  </div>
                </SelectItem>
              ))}
            </Select>
          </div>

          <div>
            <label
              htmlFor="tags-input"
              className="text-[var(--ds-text)] text-sm"
              id="tags-label"
            >
              Tags
            </label>
            <div
              className="flex flex-wrap gap-2 p-2 min-h-[40px] border bg-[var(--ds-surface)] rounded-[3px] focus-within:border-[var(--ds-border-focused)]"
              data-testid="tags-container"
            >
              {newItem.tags.map(tag => (
                <Chip
                  key={tag}
                  variant="flat"
                  onClose={() => handleRemoveTag(tag)}
                  className="bg-[var(--ds-background-neutral)] text-[var(--ds-text)]"
                  size="sm"
                  data-testid={`tag-${tag}`}
                  aria-label={`Remove tag: ${tag}`}
                >
                  {tag.toUpperCase()}
                </Chip>
              ))}
              <input
                id="tags-input"
                type="text"
                className="flex-1 bg-transparent text-sm text-[var(--ds-text)] outline-none min-w-[120px]"
                placeholder={
                  newItem.tags.length
                    ? 'Add more tags...'
                    : 'Type and press Enter or Space to add tags'
                }
                onKeyDown={handleTagInput}
                onBlur={handleTagBlur}
                data-testid="tags-input"
                aria-label="Add new tag"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="description"
              className="text-[var(--ds-text)] text-sm"
              id="description-label"
            >
              Description
            </label>
            <RichTextEditor
              value={newItem.body}
              onChange={html => setNewItem(prev => ({ ...prev, body: html }))}
              minHeight={120}
              placeholder="Enter a description..."
              dataTestId="description-input"
              ariaLabel="Description"
            />
          </div>

          {error && (
            <div
              className="text-danger text-sm"
              role="alert"
              data-testid="error-message"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="bordered"
              onPress={() => onClose()}
              className="text-[var(--ds-text)] rounded-[3px] font-bold h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
              size="sm"
              disableRipple
              data-testid="cancel-button"
              aria-label="Cancel creating new item"
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              onPress={handleCreateItem}
              isLoading={isSubmitting}
              isDisabled={licenseInfo ? !licenseInfo.canAdd : false}
              className="bg-primary text-white font-bold rounded-[3px] h-8 px-3"
              size="sm"
              disableRipple
              data-testid="create-item-button"
              aria-label="Create new item"
            >
              Create Item
            </Button>
          </div>
        </fieldset>
      </Card>
    </div>
  )
}

export default AdminNewItemPage
